// Size selector for the product page. Kept out of amb.js so the product page
// only loads what it uses. Shares the same cart plumbing: post to /cart/add.js,
// then tell Horizon's drawer through the standard CartLinesUpdateEvent.
import { CartLinesUpdateEvent } from '@shopify/events';

const RESET_MS = 1800;

async function readCart() {
  const items = document.querySelector('cart-items-component');
  if (items) {
    await customElements.whenDefined('cart-items-component');
    if (typeof items.fetchCartData === 'function') return items.fetchCartData();
  }
  const res = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return res.json();
}

async function addLine(variantId, origin, sourceId) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  origin.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
      }),
    });
    const json = await res.json();
    const failed = Boolean(json.status);
    const cart = await readCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'amb-tiers',
        sourceId,
        itemCount: 1,
        sections: json.sections,
        didError: failed,
      },
    });
    return !failed;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

function initTiers(root) {
  const inputs = [...root.querySelectorAll('[data-amb-tier-input]')];
  if (!inputs.length) return;

  const adds = [...root.querySelectorAll('[data-amb-tier-add]')];
  const labelEl = root.querySelector('[data-amb-tier-label]');
  const liveEl = root.querySelector('[data-amb-tier-live]');
  const sticky = root.querySelector('[data-amb-stickybuy]');
  const stickyName = root.querySelector('[data-amb-sticky-name]');
  const stickyPrice = root.querySelector('[data-amb-sticky-price]');
  const stickyOption = root.querySelector('[data-amb-sticky-option]');
  const stickyImg = root.querySelector('[data-amb-sticky-img]');
  const stickyChange = root.querySelector('[data-amb-sticky-change]');
  const mainAdd = root.querySelector('.amb-tiers__add');

  const idleLabel = labelEl ? labelEl.textContent.trim() : 'Add to cart';

  const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: root.dataset.currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const fmt = (cents) => money.format(cents / 100);

  const current = () => inputs.find((i) => i.checked) || inputs[0];

  function render() {
    const chosen = current();
    if (!chosen) return;
    const price = Number(chosen.dataset.price || 0);

    if (labelEl) labelEl.textContent = `${idleLabel} · ${fmt(price)}`;

    if (stickyName) stickyName.textContent = chosen.dataset.title || '';
    if (stickyPrice) stickyPrice.textContent = fmt(price);
    if (stickyOption) stickyOption.textContent = chosen.dataset.option || '';
    if (stickyImg && chosen.dataset.image) stickyImg.src = chosen.dataset.image;
  }

  inputs.forEach((input) => input.addEventListener('change', render));

  adds.forEach((button) => {
    button.addEventListener('click', async () => {
      const chosen = current();
      if (!chosen || button.disabled) return;
      adds.forEach((b) => { b.disabled = true; });
      button.dataset.state = 'adding';

      const ok = await addLine(chosen.dataset.variant, button, root.id || 'amb-tiers');

      button.dataset.state = ok ? 'added' : 'error';
      if (liveEl) liveEl.textContent = ok ? 'Added to cart' : 'Could not add to cart. Try again.';

      setTimeout(() => {
        adds.forEach((b) => { b.disabled = false; delete b.dataset.state; });
        if (liveEl) liveEl.textContent = '';
      }, RESET_MS);
    });
  });

  // "Change" in the bar jumps back up to the options.
  if (stickyChange) {
    stickyChange.addEventListener('click', () => {
      const set = root.querySelector('.amb-tiers__set') || root;
      set.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => { const c = current(); if (c) c.focus({ preventScroll: true }); }, 500);
    });
  }

  // Sticky bar, mobile and desktop: slides up once the real button has
  // scrolled up past the top of the screen, and tucks away over the footer so
  // it never covers the footer links.
  if (sticky && mainAdd) {
    const footer = document.querySelector('.amb-footer') || document.querySelector('footer');
    let shown = false;
    sticky.hidden = false;

    function setShown(on) {
      if (on === shown) return;
      shown = on;
      sticky.classList.toggle('is-shown', on);
      sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
      sticky.querySelectorAll('button').forEach((b) => { b.tabIndex = on ? 0 : -1; });
      document.documentElement.classList.toggle('amb-sticky-on', on);
    }

    function check() {
      const past = mainAdd.getBoundingClientRect().bottom < 0;
      const atFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      setShown(past && !atFooter);
    }
    // Checked straight on scroll (two cheap reads), so it never waits on a frame.
    const ask = () => check();

    sticky.setAttribute('aria-hidden', 'true');
    sticky.querySelectorAll('button').forEach((b) => { b.tabIndex = -1; });
    // Capture phase: this theme scrolls inside .page-wrapper, not the window.
    document.addEventListener('scroll', ask, { capture: true, passive: true });
    window.addEventListener('resize', ask);
    check();
  }

  render();
}

function boot() {
  document.querySelectorAll('[data-amb-tiers]').forEach((root) => {
    if (root.dataset.ambBooted === 'true') return;
    root.dataset.ambBooted = 'true';
    initTiers(root);
  });
}

boot();
document.addEventListener('shopify:section:load', boot);
document.addEventListener('shopify:block:select', boot);
